import {
    getLocale,
    ServiceLanguage,
    ServiceLanguageToLocaleMap,
} from "@albertine/i18n";
import type { ReactNode } from "react";
import { I18nextProvider, Trans, useTranslation } from "react-i18next";
import i18n from "./i18n";

type TranslationProviderProps = {
    children: ReactNode;
    initialLanguage?: ServiceLanguage;
};

export function AlbertineI18nProvider(props: TranslationProviderProps) {
    const { children, initialLanguage } = props;
    if (initialLanguage && i18n.language !== initialLanguage) {
        void i18n.changeLanguage(initialLanguage);
    }

    return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}

export function useAlbertineTranslation() {
    const { t } = useTranslation();

    return t;
}

export function changeUILanguage(locale: ServiceLanguage) {
    void i18n.changeLanguage(locale);
}

export const translation = i18n.t;

export function getUILanguage() {
    const lang = getLocale(i18n.language);
    return {
        lang,
        dateFnsLocale: ServiceLanguageToLocaleMap[lang],
    };
}

export function getUILanguageFromUrl(): ServiceLanguage {
    const searchParams = new URLSearchParams(window.location.search);
    const langParam = searchParams.get("lang");
    const serviceLanguage: ServiceLanguage =
        ServiceLanguage.safeParse(langParam).success && langParam
            ? (langParam as ServiceLanguage)
            : getUILanguage().lang;
    return serviceLanguage;
}

export function useUILanguage(): ServiceLanguage {
    const {
        i18n: { language },
    } = useTranslation();

    return getLocale(language);
}

/**
 * Detects browser language and returns first matching language allowed languages in the tenant.
 */
export function findServiceLanguageFromBrowser(
    allowedServiceLanguages: ServiceLanguage[],
) {
    const browserLanguages = navigator.languages ? navigator.languages : [];
    const splitBrowserLanguages = browserLanguages.map((lang) => lang.split("-")[0]);
    const filteredLanguages = splitBrowserLanguages.filter((browserLanguage) =>
        allowedServiceLanguages.some(
            (serviceLanguage) => serviceLanguage === browserLanguage,
        ),
    );

    return getLocale(filteredLanguages[0]);
}

/**
 * Tries to detect the region from the browser language settings.
 *
 * Works for languages where the region is specified (e.g. "en-US" or "en-GB").
 *
 * Does not work for languages where region is not specified at all (e.g. "fi" instead of "fi-FI"), in which case we default to UK
 */
export function guessRegionFromBrowser(): string {
    const defaultRegion = "UK";

    if (!navigator.language) return defaultRegion;

    try {
        const locale = new Intl.Locale(navigator.language);
        return locale.region ?? defaultRegion;
    } catch {
        return defaultRegion;
    }
}

export { Trans };
